import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

const AppointmentConfirmed = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [appointmentData, setAppointmentData] = useState<any>(null);

  useEffect(() => {
    const confirmAppointment = async () => {
      const id = searchParams.get("id");
      
      if (!id) {
        setStatus("error");
        return;
      }

      try {
        // Call the edge function
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/confirm-appointment?id=${id}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          // Fetch appointment details
          const { data } = await supabase
            .from('appointments')
            .select('*')
            .eq('id', id)
            .single();
          
          setAppointmentData(data);
          setStatus("success");
        } else {
          setStatus("error");
        }
      } catch (error) {
        console.error('Error confirming appointment:', error);
        setStatus("error");
      }
    };

    confirmAppointment();
  }, [searchParams]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-secondary/20">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin mx-auto mb-4 text-primary" />
          <h2 className="text-2xl font-bold mb-2">Termin wird bestätigt...</h2>
          <p className="text-muted-foreground">Bitte warten Sie einen Moment.</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-secondary/20 p-4">
        <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-10 h-10 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold mb-2 text-destructive">Ups! Fehler bei der Bestätigung</h1>
          <p className="text-muted-foreground mb-6">
            Es gab einen Fehler beim Bestätigen des Termins. Bitte versuchen Sie es erneut oder kontaktieren Sie uns.
          </p>
          <Button
            onClick={() => window.location.href = '/admin/kalender'}
            variant="outline"
          >
            Zum Admin-Kalender
          </Button>
        </div>
      </div>
    );
  }

  const formattedDate = appointmentData ? new Date(appointmentData.preferred_date).toLocaleDateString('de-DE', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : '';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-secondary/20 p-4">
      <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-10 h-10 text-green-500" />
        </div>
        <h1 className="text-2xl font-bold mb-2 text-green-600">Termin erfolgreich bestätigt!</h1>
        <p className="text-muted-foreground mb-6">
          Der Kunde wurde per E-Mail benachrichtigt und erhält den Teams-Link.
        </p>
        
        {appointmentData && (
          <>
            <div className="bg-muted/50 rounded-lg p-4 mb-4 text-left">
              <h2 className="font-semibold mb-3 text-center">Termindetails</h2>
              <div className="space-y-2 text-sm">
                <p><strong>Kunde:</strong> {appointmentData.name}</p>
                <p><strong>E-Mail:</strong> {appointmentData.email}</p>
                <p><strong>Datum:</strong> {formattedDate}</p>
                <p><strong>Uhrzeit:</strong> {appointmentData.preferred_time} Uhr</p>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
              <p className="text-sm font-semibold mb-2 flex items-center justify-center gap-2">
                <CalendarIcon className="w-4 h-4" />
                ✓ Termin wird automatisch in Ihren Kalender eingetragen
              </p>
              <p className="text-xs text-muted-foreground">
                Wenn Sie den Kalender-Feed abonniert haben, erscheint dieser Termin automatisch in Outlook oder Google Kalender.
              </p>
            </div>
          </>
        )}

        <Button
          onClick={() => window.location.href = '/admin/kalender'}
          className="w-full"
        >
          Zum Admin-Kalender
        </Button>
      </div>
    </div>
  );
};

export default AppointmentConfirmed;
